/**
 * OfflinePage, full-screen offline fallback.
 *
 * Styled to match FeedbackPage v2 vocabulary: canvas #FAFAF7, rounded-[20px]
 * card, typography system. The inline offline banner on FeedbackPage handles
 * the "you can still submit" case; this full-screen view is only reached when
 * the SW serves the offline fallback for a fresh navigation.
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { WifiOff, RefreshCw, Cloud } from 'lucide-react';
import { getQueueCount } from '@/lib/offlineQueue';
import { LanguageSwitcher } from '@/components/ui/LanguageSwitcher';

export function OfflinePage() {
  const { t } = useTranslation('visitor');
  const [queuedCount, setQueuedCount] = useState(0);
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    getQueueCount().then(setQueuedCount);

    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const handleRetry = () => {
    window.location.reload();
  };

  return (
    <div
      className="min-h-[100dvh] flex flex-col items-center justify-center px-5 py-8"
      style={{ background: '#FAFAF7' }}
    >
      <div className="absolute top-4 end-4">
        <LanguageSwitcher compact />
      </div>

      <div className="w-full max-w-[440px]">
        <div className="rounded-[24px] bg-white p-7 shadow-[0_4px_12px_-4px_rgba(10,14,20,0.06)]">
          <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-2xl bg-[#F3F4F0]">
            <WifiOff className="h-8 w-8 text-[#5A6472]" aria-hidden="true" />
          </div>

          <h1 className="text-center text-[24px] font-bold leading-tight tracking-[-0.02em] text-[#0A0E14]">
            {t('offline.title')}
          </h1>
          <p className="mt-2 text-center text-[16px] font-medium leading-relaxed text-[#5A6472]">
            {t('offline.description')}
          </p>

          {queuedCount > 0 ? (
            <div
              className="mt-5 flex items-start gap-3 rounded-2xl p-4"
              style={{ background: 'rgba(14,124,123,0.06)', borderColor: 'rgba(14,124,123,0.15)' }}
            >
              <Cloud className="h-5 w-5 flex-shrink-0 text-[#0E7C7B] mt-0.5" aria-hidden="true" />
              <div className="flex-1">
                <p className="text-[16px] font-semibold text-[#0A0E14]">
                  {t('offline.waitingCount', { count: queuedCount })}
                </p>
                <p className="mt-0.5 text-[15px] text-[#5A6472]">{t('offline.willSyncAutomatically')}</p>
              </div>
            </div>
          ) : (
            <p className="mt-5 text-center text-[15px] text-[#5A6472]">{t('offline.autoSyncMessage')}</p>
          )}

          <div
            className={`mt-5 flex items-center justify-center gap-2 rounded-full px-4 py-2 ${
              isOnline ? 'bg-emerald-50 text-emerald-700' : 'bg-[#F3F4F0] text-[#5A6472]'
            }`}
          >
            <div
              className={`h-2 w-2 rounded-full ${isOnline ? 'bg-emerald-500 animate-pulse' : 'bg-[#5A6472]'}`}
              aria-hidden="true"
            />
            <span className="text-[15px] font-medium">
              {isOnline ? t('offline.connectionRestored') : t('offline.noConnection')}
            </span>
          </div>

          <button
            type="button"
            onClick={handleRetry}
            className="mt-5 flex w-full items-center justify-center gap-2 rounded-[20px] px-4 py-[16px] text-[16px] font-semibold text-white transition-transform active:scale-[0.98]"
            style={{ background: isOnline ? '#0E7C7B' : '#5A6472' }}
          >
            <RefreshCw className="h-4 w-4" aria-hidden="true" />
            {isOnline ? t('offline.continue') : t('common:buttons.retry')}
          </button>
        </div>
      </div>
    </div>
  );
}
